import { usePageRefreshKey } from '../../../context/PageRefreshContext';
import { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import { getShifts, getShiftEnums, updateShift } from '../../../redux/actions/SocietyPortal/ShiftAction/ShiftAction';
import { PaginationLimit } from '../../../components/PaginationLimit';
import SearchableDropdown from '../../../components/SearchableDropdown';
import usePermissions from '../../../hook/usePermissions';
import { PERMISSIONS } from '../../../consts/permissions';
import {
    toBool,
    formatWallTime,
    formatMinutes,
    readableTextOn,
} from '../../../consts/shiftConstants';
import ShiftFormModal from './Component/ShiftFormModal';
import DeleteShiftModal from './Component/DeleteShiftModal';

// Shift setup — the templates everything else is built from. A shift says what
// a working day looks like; an assignment says who works one, from when. That
// is the whole model: there is no roster or day-by-day calendar behind it.

const selectClass = 'tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500';

const STATUS_OPTIONS = [
    { value: 'true', label: 'Active' },
    { value: 'false', label: 'Inactive' },
];

const Shifts = () => {
    const dispatch = useDispatch();
    const { can } = usePermissions();
    const canManage = can(PERMISSIONS.SHIFT_MANAGE);

    const { shifts, shiftsPagination, shiftsLoading } = useSelector(state => state.ShiftReducer || {});

    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [activeFilter, setActiveFilter] = useState('');
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [refreshTick, setRefreshTick] = useState(0);

    const [formShift, setFormShift] = useState(null); // null = closed, {} = new
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [restoringId, setRestoringId] = useState(null);

    const refresh = () => setRefreshTick(t => t + 1);

    // Restoring is just the reverse of "Deactivate instead" — flip is_active back on
    const handleRestore = (shift) => {
        setRestoringId(shift.shift_id);
        dispatch(updateShift({
            shift_id: shift.shift_id,
            is_active: true,
            onSuccess: () => { setRestoringId(null); refresh(); },
            onError: () => setRestoringId(null),
        }));
    };

    useEffect(() => { dispatch(getShiftEnums()); }, [dispatch]);

    useEffect(() => {
        const id = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 400);
        return () => clearTimeout(id);
    }, [searchInput]);

    const refreshKey = usePageRefreshKey();
    useEffect(() => {
        dispatch(getShifts({
            search,
            is_active: activeFilter === '' ? undefined : activeFilter,
            page,
            limit,
        }));
    }, [dispatch, search, activeFilter, page, limit, refreshTick, refreshKey]);

    const pg = shiftsPagination || {};

    return (
        <>
            <div className="tw-flex tw-items-start tw-justify-between tw-mb-4 tw-gap-3 tw-flex-wrap">
                <div>
                    <h1 className="tw-text-lg tw-font-bold tw-text-gray-800">Shift Setup</h1>
                    <p className="tw-text-xs tw-text-gray-400 tw-mt-0.5">
                        Define the shifts first — employees are assigned to one from their own record
                    </p>
                </div>
                {canManage && (
                    <button
                        type="button"
                        onClick={() => setFormShift({})}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-blue-600 tw-rounded-md hover:tw-bg-blue-700"
                    >
                        <Icon icon="mdi:plus" /> New shift
                    </button>
                )}
            </div>

            <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4 tw-mb-4">
                <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-4 tw-gap-3">
                    <div className="tw-col-span-2">
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Search</label>
                        <input
                            type="text"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            placeholder="Shift name or code…"
                            className={selectClass}
                        />
                    </div>
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Status</label>
                        <SearchableDropdown
                            small
                            value={activeFilter}
                            // Clearing hands back 'all' — the filter treats that as no filter
                            onChange={(val) => { setActiveFilter(val === 'all' ? '' : val); setPage(1); }}
                            options={STATUS_OPTIONS}
                            placeholder="All"
                        />
                    </div>
                </div>
            </div>

            <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-overflow-hidden">
                <div className="tw-overflow-x-auto">
                    <table className="tw-w-full tw-text-xs">
                        <thead>
                            <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-200">
                                {['Shift', 'Timing', 'Span / working', 'Break', 'Status', ''].map((h, i) => (
                                    <th key={i} className="tw-px-3 tw-py-2.5 tw-text-left tw-text-[11px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wider tw-whitespace-nowrap">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {shiftsLoading ? (
                                <tr><td colSpan={6} className="tw-text-center tw-py-12"><Icon icon="mdi:loading" className="tw-animate-spin tw-text-2xl tw-text-gray-300 tw-mx-auto" /></td></tr>
                            ) : (shifts || []).length === 0 ? (
                                <tr><td colSpan={6} className="tw-text-center tw-py-12">
                                    <Icon icon="hugeicons:time-schedule" className="tw-text-4xl tw-text-gray-200 tw-mx-auto" />
                                    <p className="tw-text-xs tw-text-gray-400 tw-mt-2 tw-mb-0">No shifts yet — add one before assigning employees.</p>
                                    {canManage && (
                                        <button type="button" onClick={() => setFormShift({})} className="tw-text-xs tw-text-blue-600 tw-mt-2 hover:tw-underline">
                                            Create the first one
                                        </button>
                                    )}
                                </td></tr>
                            ) : (
                                shifts.map(shift => (
                                    <tr key={shift.shift_id} className="tw-border-b tw-border-gray-100 hover:tw-bg-gray-50/50">
                                        <td className="tw-px-3 tw-py-2">
                                            <div className="tw-flex tw-items-center tw-gap-2">
                                                <span
                                                    className="tw-px-1.5 tw-py-0.5 tw-rounded tw-text-[10px] tw-font-bold tw-tracking-wide"
                                                    style={{
                                                        backgroundColor: shift.color_hex || '#E5E7EB',
                                                        color: readableTextOn(shift.color_hex),
                                                    }}
                                                >
                                                    {shift.shift_code}
                                                </span>
                                                <div>
                                                    <div className="tw-font-medium tw-text-gray-800">{shift.shift_name}</div>
                                                    {toBool(shift.is_default) && (
                                                        <span className="tw-text-[10px] tw-text-blue-600">Default shift</span>
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                        <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-700">
                                            {formatWallTime(shift.start_time)} → {formatWallTime(shift.end_time)}
                                            {/* A night shift ends the next morning */}
                                            {toBool(shift.crosses_midnight) && (
                                                <span className="tw-ml-1 tw-text-[10px] tw-text-indigo-600 tw-font-medium" title="Ends the next day">+1</span>
                                            )}
                                        </td>
                                        <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-600">
                                            {/* Derived server-side — displayed, never recomputed */}
                                            {shift.span_display || formatMinutes(shift.span_minutes)}
                                            <span className="tw-text-gray-300"> / </span>
                                            <span className="tw-font-medium tw-text-gray-800">
                                                {shift.scheduled_display || formatMinutes(shift.scheduled_minutes)}
                                            </span>
                                        </td>
                                        <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-600">
                                            {shift.break_minutes ? `${shift.break_minutes}m` : '—'}
                                            {shift.break_minutes ? (
                                                <span className="tw-text-[10px] tw-text-gray-400"> {shift.break_type === 'PAID' ? '(paid)' : ''}</span>
                                            ) : null}
                                        </td>
                                        <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap">
                                            <span className={`tw-inline-flex tw-px-2 tw-py-0.5 tw-rounded tw-text-[11px] tw-font-medium ${toBool(shift.is_active) ? 'tw-bg-emerald-50 tw-text-emerald-700' : 'tw-bg-gray-100 tw-text-gray-500'}`}>
                                                {toBool(shift.is_active) ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-right">
                                            {canManage && (
                                                <div className="tw-flex tw-gap-1 tw-justify-end">
                                                    {!toBool(shift.is_active) && (
                                                        <button
                                                            type="button"
                                                            onClick={() => handleRestore(shift)}
                                                            disabled={restoringId === shift.shift_id}
                                                            className="tw-px-2 tw-py-1 tw-rounded tw-text-[11px] tw-font-medium tw-text-emerald-700 tw-bg-emerald-50 hover:tw-bg-emerald-100 disabled:tw-opacity-50"
                                                        >
                                                            {restoringId === shift.shift_id ? 'Restoring…' : 'Restore'}
                                                        </button>
                                                    )}
                                                    <button
                                                        type="button"
                                                        onClick={() => setFormShift(shift)}
                                                        className="tw-px-2 tw-py-1 tw-rounded tw-text-[11px] tw-font-medium tw-text-gray-600 tw-bg-gray-100 hover:tw-bg-gray-200"
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setDeleteTarget(shift)}
                                                        className="tw-px-2 tw-py-1 tw-rounded tw-text-[11px] tw-font-medium tw-text-red-700 tw-bg-red-50 hover:tw-bg-red-100"
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {pg.total > 0 && (
                <PaginationLimit
                    currentPage={page}
                    totalRecords={pg.total || 0}
                    limit={limit}
                    handleLimitChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                    handlePageChange={(p) => setPage(p)}
                />
            )}

            <ShiftFormModal
                isOpen={Boolean(formShift)}
                shift={formShift}
                onClose={() => setFormShift(null)}
                onDone={refresh}
                // The 409 hands back the shift that already owns the code —
                // jump to it rather than leaving the admin to find it.
                onCodeTaken={(shiftId) => {
                    const existing = (shifts || []).find(s => s.shift_id === shiftId);
                    if (existing) {
                        setFormShift(existing);
                    }
                }}
            />
            <DeleteShiftModal
                shift={deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onDone={refresh}
            />
        </>
    );
};

export default Shifts;
